import CloseIcon from '@mui/icons-material/Close'
import { Badge, Box, Button, Divider, List, ListItem, ListItemButton, ListItemIcon, ListItemText, ListSubheader, MenuItem, Popper, Stack, Typography, useTheme } from '@mui/material'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import IconButton from '@mui/material/IconButton'
import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import AnalyticsManager from '../../analytics/AnalyticsManager'
import { ACCESS_TOKEN, BROKERS_LOGOS_BASE_URL } from '../../assets/constants'
import Add from '../../assets/icons/Add'
import '../../assets/styles/index.css'
import { Broker, RemoveTradingAccountRequest, TradingAccount, TradingAccountMargin, TradingAccountStatus } from '../../proto/generated/trading-bridge'
import { WebSocketResponseMessage } from '../../proto/generated/websocket'
import { setBrokers, setMarginForAccount, setTradingAccounts } from '../../reducer/user.reducer'
import { selectBrokers, selectMargins, selectPrimaryTradingAccount, selectTradingAccounts } from '../../selectors/user'
import { getAccountMargins, getBrokers, removeTradingAccount, setPrimaryTradingAccountApi } from '../../services/apis/tradingAccount'
import { BrokerId, generateBrokerLoginUrl, keyValueArrayFromTradingAccountParams } from '../../util/tradingAccounts'
import { WebSocketContext } from '../webSocketConnection/WebSocket'
import PaperTradingLogo from '../../assets/icons/PaperTradingLogo'
import DraggableDialogTitle from '../common/DraggableDialogTitle'
import ROUTES from '../../assets/routes'
import { darkBg, white } from '../../assets/colors'
import { closeConnectAccountDialog } from '../../reducer/orders.reducer'
interface TradingAccountBarProps {
    hovered: boolean
    isOpen: boolean
    onCloseSidebar?: () => void;
}

const TradingAccountBar = ({ hovered, isOpen, onCloseSidebar }: TradingAccountBarProps) => {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const ws = useContext(WebSocketContext)
    const [loading, setLoading] = useState<boolean>(true)
    const tradingAccounts: TradingAccount[] = useSelector(selectTradingAccounts)
    const primaryTradingAccount = useSelector(selectPrimaryTradingAccount)
    const brokers = useSelector(selectBrokers)
    const brokersRef = useRef<Broker[]>([])
    const tradingMarginRef = useRef<TradingAccountMargin | undefined>(undefined)
    const fallbackRef = useRef(true)
    const margins = useSelector(selectMargins)
    const [openRemoveAccountDialog, setOpenRemoveAccountDialog] = useState<boolean>(false)
    const [accountClicked, setAccountClicked] = useState<TradingAccount>()
    const [listItemHovered, setListItemHovered] = useState(false);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
    const theme = useTheme()
    const onWebSocketMessageReceived = useCallback(
        (data: Uint8Array) => {
            const responseData = WebSocketResponseMessage.decode(data)
            if (responseData.tradingAccountMargin && primaryTradingAccount?.accountId === responseData.tradingAccountMargin.accountId) {
                dispatch(setMarginForAccount({ accountId: primaryTradingAccount.accountId, margin: responseData.tradingAccountMargin }))
                fallbackRef.current = false
            }
        },
        [dispatch, primaryTradingAccount?.accountId]
    )

    useEffect(() => {
        if (ws !== null) {
            ws!.addListener(onWebSocketMessageReceived)
        }
        return () => {
            if (ws !== null) {
                ws!.removeListener(onWebSocketMessageReceived)
            }
        }
    }, [onWebSocketMessageReceived, ws])

    useEffect(() => {
        brokersRef.current = brokers
        const token = localStorage.getItem(ACCESS_TOKEN)
        if (token && brokers.length === 0) {
            setTimeout(async () => {
                if (brokersRef.current.length === 0) {
                    const brokers: Broker[] = await fetchBrokers()
                    dispatch(setBrokers(brokers))
                }
                setLoading(false)
            }, 1000)
        }
    }, [brokers, dispatch])

    useEffect(() => {
        if (brokers.length !== 0) {
            setLoading(false)
        }
    }, [brokers])

    useEffect(() => {
        if (!hovered) {
            setListItemHovered(false)
            setAnchorEl(null)
        }
    }, [hovered])

    const fetchMargins = useCallback(async (tradingAccount: TradingAccount) => {
        try {
            const body = {
                accountId: tradingAccount.accountId,
                brokerCode: tradingAccount.brokerCode,
                brokerAccountId: tradingAccount.brokerAccountId,
                created: tradingAccount.created
            }
            const response = await getAccountMargins({
                body: body
            })
            dispatch(setMarginForAccount({ accountId: tradingAccount.accountId, margin: response }))
            return response
        } catch (error) {
            console.error('Error fetching account margins', error)
        }
    }, [])

    useEffect(() => {
        if (fallbackRef.current) {
            //  fallback
            setTimeout(
                () =>
                    (async () => {
                        tradingMarginRef.current = primaryTradingAccount?.accountId ? margins[primaryTradingAccount.accountId] : undefined
                        if (fallbackRef.current && primaryTradingAccount) {
                            await fetchMargins(primaryTradingAccount)
                            fallbackRef.current = false
                        }
                    })(),
                1000
            )
        }
    }, [fetchMargins, primaryTradingAccount, tradingAccounts])

    const handleRemoveTradingAccountDialogClose = () => {
        setOpenRemoveAccountDialog(false)
    }
    const fetchBrokers = useCallback(async (): Promise<Broker[]> => {
        try {
            const response = await getBrokers()
            return response.brokers.filter((broker) => broker.enabled === true)
        } catch (error) {
            console.error('Error fetching brokers', error)
            return []
        }
    }, [])

    const deleteTradingAccount = useCallback(async () => {
        AnalyticsManager.logEvent('Remove trading account clicked', {
            tradingAccount: accountClicked?.brokerCode ? accountClicked.brokerCode : undefined
        })
        if (accountClicked) {
            const body: RemoveTradingAccountRequest = Object.assign(
                {},
                {
                    brokerCode: accountClicked.brokerCode,
                    tradingAccountId: accountClicked.accountId
                }
            )
            const response = await removeTradingAccount({ body: body })
            dispatch(setTradingAccounts(response.tradingAccounts))
            setOpenRemoveAccountDialog(false)
        }
    }, [accountClicked, dispatch])

    const brokerImageUrlFromCode = useCallback((code: string) => {
        const broker = brokers.find(b => b.code === code)
        return broker ? BROKERS_LOGOS_BASE_URL + broker.imageUrl : ''
    }, [brokers])

    const handleConnectBrokenAccount = useCallback(async (tradingAccount: TradingAccount) => {
        if (tradingAccount && tradingAccount.status !== TradingAccountStatus.ACTIVE) {
            if (tradingAccount.brokerCode !== BrokerId.DHAN) {
                const fetchedUrl = await generateBrokerLoginUrl(tradingAccount.brokerCode, keyValueArrayFromTradingAccountParams(tradingAccount.tradingAccountParameters))
                window.open(fetchedUrl, '_self')
            }
        }
    }, [brokers])

    const changePrimaryTradingAccount = useCallback(
        async (account: TradingAccount) => {
            if (account.brokerAccountId !== primaryTradingAccount?.brokerAccountId) {
                try {
                    await setPrimaryTradingAccountApi({
                        body: {
                            tradingAccountId: account.accountId
                        }
                    })
                } catch (e) {
                    console.error(e, 'error in change primary account')
                }
            }
            if (account.status === TradingAccountStatus.BROKEN) {
                handleConnectBrokenAccount(account)
            }
            dispatch(closeConnectAccountDialog())
            setAccountClicked(account);
            setListItemHovered(false);
            setAnchorEl(null);
            onCloseSidebar?.();
        },
        [dispatch, handleConnectBrokenAccount, primaryTradingAccount])

    return loading ? null : (
        <List disablePadding sx={{
            background: (theme) => listItemHovered ? theme.palette.background.default : 'inherit'
        }}>
            {!tradingAccounts?.length ? (
                <ListItemButton
                    data-event={'Add Trading Account Clicked'}
                    data-event-source={'left-side-bar'}
                    id={'broker-integration-button'}
                    onClick={() => navigate(ROUTES.tradingAccount)}

                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        textTransform: 'none',
                        height: '50px',
                        justifyContent: 'start',
                        '&:hover': { background: (theme) => theme.palette.background.default },
                        padding: '0px 8px '
                    }}
                >
                    <ListItemIcon>
                        <Add height={40} width={40} />
                    </ListItemIcon>
                    {hovered && <ListItemText primaryTypographyProps={{ fontSize: '14px' }} primary="Connect Broker" />}
                </ListItemButton>
            ) : (
                <>

                    <ListItem
                        onMouseEnter={(e) => {
                            setListItemHovered(true)
                            setAnchorEl(e.currentTarget)
                        }}
                        onMouseLeave={() => {
                            setListItemHovered(false)
                        }}
                        id={'broker-integration-button'}
                        sx={{
                            display: 'flex',
                            textTransform: 'none',
                            justifyContent: 'start',
                            width: 'max-content',
                            padding: '4px 10px',
                            cursor: 'pointer'
                        }}
                        onClick={() => {
                            setAccountClicked(primaryTradingAccount)
                            navigate(ROUTES.tradingAccount)
                        }}
                    >
                        <ListItemIcon
                            data-event={'Trading Account Summary Click'}
                            sx={{
                                color: (theme) => theme.palette.text.secondary,
                                minWidth: '30px'
                            }}
                        >
                            {primaryTradingAccount && primaryTradingAccount.brokerCode === BrokerId.PAPER_TRADING ? (
                                <Box>
                                    <PaperTradingLogo fontSize={'large'} />
                                </Box>
                            ) : (
                                <Badge color={'error'} variant="dot" overlap="circular" invisible={!(primaryTradingAccount && primaryTradingAccount.status !== TradingAccountStatus.ACTIVE)}>
                                    <Box
                                        component="img"
                                        sx={{ height: '35px', width: '35px', borderRadius: primaryTradingAccount && primaryTradingAccount.brokerCode === BrokerId.DHAN ? '100%' : '0%' }}
                                        src={primaryTradingAccount && primaryTradingAccount.brokerCode ? brokerImageUrlFromCode(primaryTradingAccount.brokerCode) : undefined}
                                        alt={'broker'}
                                    />
                                </Badge>
                            )}
                        </ListItemIcon>
                        {hovered && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    alignItems: 'center',
                                    paddingLeft: '0px',
                                    width: '190px',
                                    overflow: 'hidden',
                                    marginLeft: '6px'
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        overflow: 'hidden',
                                        flexGrow: 1
                                    }}
                                >
                                    <ListItemText>{primaryTradingAccount?.displayName ?? 'Add Account'}</ListItemText>
                                </Box>
                            </Box>
                        )}
                    </ListItem>

                    <Popper
                        open={listItemHovered && Boolean(anchorEl)}
                        anchorEl={anchorEl}
                        placement="right-start"
                        onMouseEnter={() => setListItemHovered(true)}
                        onMouseLeave={() => {
                            setListItemHovered(false);
                            setAnchorEl(null);
                        }}
                        modifiers={[
                            {
                                name: 'offset',
                                options: {
                                    offset: [0, -21],
                                },
                            },
                        ]}
                        sx={{
                            bgcolor: theme => theme.palette.mode === 'dark' ? '#101010' : white,
                            p: 0,
                            borderRadius: 1,
                            boxShadow: 3,
                            zIndex: 1300,
                            pointerEvents: 'auto',
                        }}
                    >
                        <Box sx={{ py: 1, minWidth: 170, paddingBottom: "4px !important" }}>
                            {tradingAccounts?.length === 0 ? (
                                <MenuItem
                                    onClick={() => {
                                        navigate(ROUTES.tradingAccount);
                                        setListItemHovered(false);
                                        setAnchorEl(null);
                                        onCloseSidebar?.();
                                    }}
                                >
                                    <ListItemIcon>
                                        <Add height={40} width={40} />
                                    </ListItemIcon>
                                    Connect Broker
                                </MenuItem>
                            ) : (
                                <>
                                    <ListSubheader
                                        sx={{
                                            lineHeight: '24px',
                                            backgroundColor: 'unset',
                                            fontSize: 11,
                                            opacity: 0.5,
                                            borderTop: 1,
                                            borderColor: theme => theme.palette.mode === 'dark' ? darkBg : white
                                        }}
                                    >
                                        SWITCH ACCOUNTS
                                    </ListSubheader>
                                    {[...tradingAccounts]
                                        .sort((a, b) => {
                                            if (a.primary && !b.primary) return -1;
                                            if (!a.primary && b.primary) return 1;
                                            return 0;
                                        }).map((account) => {
                                            return (
                                                <MenuItem
                                                    key={account.accountId}
                                                    onClick={() => {changePrimaryTradingAccount(account)}}
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        p: 1,
                                                    }}
                                                >
                                                    {account && account.brokerCode === BrokerId.PAPER_TRADING ? (
                                                        <PaperTradingLogo sx={{ fontSize: '25px', marginRight: "5px" }} />
                                                    ) : (

                                                        <Box
                                                            component="img"
                                                            sx={{ height: '25px', width: '25px', marginRight: "5px", borderRadius: account && account.brokerCode === BrokerId.DHAN ? '100%' : '0%' }}
                                                            src={account && account.brokerCode ? brokerImageUrlFromCode(account.brokerCode) : undefined}
                                                            alt="broker"
                                                        />

                                                    )}
                                                    <Typography variant="body2" noWrap fontSize={'14px'} sx={{ flexGrow: 1 }}>
                                                        {account.displayName}
                                                    </Typography>
                                                    <Badge sx={{ mr: 1 }} color={account.status !== TradingAccountStatus.ACTIVE ? 'error' : "success"} variant="dot" overlap="circular" ></Badge>
                                                </MenuItem>
                                            );
                                        })}
                                    <Divider
                                        sx={{
                                            margin: "0px !important",
                                            backgroundColor: theme.palette.mode === 'dark' ? '#434651' : '#e0e3eb'
                                        }}
                                    />
                                    <MenuItem
                                        onClick={() => {
                                            navigate(ROUTES.tradingAccount);
                                            setListItemHovered(false);
                                            setAnchorEl(null);
                                            setListItemHovered(false);
                                            setAnchorEl(null);
                                            onCloseSidebar?.()
                                        }}
                                    >
                                        <Typography fontSize={'14px'} p={0.5}>Manage Accounts</Typography>
                                    </MenuItem>
                                </>
                            )}
                        </Box>
                    </Popper> </>
            )}
            <Dialog open={openRemoveAccountDialog} onClose={handleRemoveTradingAccountDialogClose} aria-labelledby="alert-dialog-title" aria-describedby="alert-dialog-description">
                <DraggableDialogTitle id="alert-dialog-title">{'Remove Trading Account'}</DraggableDialogTitle>
                <IconButton
                    aria-label="close"
                    onClick={handleRemoveTradingAccountDialogClose}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 12
                    }}
                >
                    <CloseIcon />
                </IconButton>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">{'Are you sure you want to remove trading account?'}</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleRemoveTradingAccountDialogClose}>{'No'}</Button>
                    <Button onClick={deleteTradingAccount}>{'Yes'}</Button>
                </DialogActions>
            </Dialog>
        </List>
    )
}
export default TradingAccountBar
